import { JOURNAL_PAGE_BASE_SIZE } from './journalPageTexture';

/** The prop kinds (the four spec tiers are complete at 21/21; gnarled_tree is
 *  an authored-only Lanternwood landmark).
 *  Tier 1 = pig_beach, Tier 1.5 = tidewend (amber dusk village),
 *  Tier 2 = brackenmoor (cold grey-green, the path has greyed out),
 *  Tier 3 = coil_undercroft (near-black, sickly-green, the lighthouse interior). */
export type PropKind =
  | 'palm_tree' | 'beach_rock' | 'tide_pool' | 'trinket' | 'pig_pen'
  | 'village_house' | 'village_well' | 'lantern_post' | 'dock' | 'autumn_leaf'
  | 'dead_bracken' | 'moor_stone' | 'mist_shelf' | 'fallen_log' | 'gate' | 'journal_page'
  | 'colonnade_column' | 'broken_pillar' | 'dais' | 'spiral_ledge' | 'bioluminescent_fungus'
  | 'gnarled_tree' | 'sign';

/** All prop kinds, for iteration/registries. */
export const PROP_KINDS: readonly PropKind[] = [
  'palm_tree', 'beach_rock', 'tide_pool', 'trinket', 'pig_pen',
  'village_house', 'village_well', 'lantern_post', 'dock', 'autumn_leaf',
  'dead_bracken', 'moor_stone', 'mist_shelf', 'fallen_log', 'gate', 'journal_page',
  'colonnade_column', 'broken_pillar', 'dais', 'spiral_ledge', 'bioluminescent_fungus',
  'gnarled_tree', 'sign',
];

const PROP_KIND_SET: ReadonlySet<string> = new Set(PROP_KINDS);

/** Is this vegetation kind a prop (vs core tree/bush/grass)? */
export function isPropKind(kind: string): kind is PropKind {
  return PROP_KIND_SET.has(kind);
}

/** Intrinsic SVG dimensions per kind (aspect must match naturePlacement's
 *  KIND_ASPECT so sprites scale without distortion). */
export const PROP_BASE_SIZE: Record<PropKind, { w: number; h: number }> = {
  sign: { w: 72, h: 44 },
  palm_tree: { w: 90, h: 120 },  // aspect 0.75
  beach_rock: { w: 64, h: 40 },  // aspect 1.6
  tide_pool: { w: 96, h: 32 },   // aspect 3.0
  trinket: { w: 24, h: 24 },     // aspect 1.0
  pig_pen: { w: 110, h: 46 },    // aspect ~2.4
  village_house: { w: 90, h: 100 }, // aspect 0.9
  village_well: { w: 64, h: 80 },   // aspect 0.8
  lantern_post: { w: 56, h: 112 },  // aspect 0.5
  dock: { w: 140, h: 56 },          // aspect 2.5
  autumn_leaf: { w: 20, h: 20 },    // aspect 1.0
  dead_bracken: { w: 44, h: 56 },   // aspect 0.786 (angular twisted bush)
  moor_stone: { w: 58, h: 42 },     // aspect 1.381 (angular, less squat than beach_rock)
  mist_shelf: { w: 100, h: 32 },    // aspect 3.125 (flat pooled-fog ledge)
  fallen_log: { w: 120, h: 38 },    // aspect 3.158 (long horizontal log)
  gate: { w: 68, h: 92 },           // aspect 0.739 (fence-post landmark)
  journal_page: JOURNAL_PAGE_BASE_SIZE, // aspect 2.143 (a page lying flat under a stone; D41 2x)
  colonnade_column: { w: 46, h: 140 },        // aspect 0.329 (tall leaning interior support)
  broken_pillar: { w: 112, h: 56 },           // aspect 2.0 (snapped stub + toppled top-half)
  dais: { w: 140, h: 48 },                    // aspect 2.917 (raised circular boss platform)
  spiral_ledge: { w: 100, h: 32 },            // aspect 3.125 (curved spiral-staircase edge)
  bioluminescent_fungus: { w: 26, h: 20 },    // aspect 1.3 (tiny glowing wall cluster)
  gnarled_tree: { w: 120, h: 160 },            // aspect 0.75 (bare asymmetric landmark)
};

/** Spec-primary tint per kind (the profile palette default). */
export const PROP_DEFAULT_TINT: Record<PropKind, number> = {
  sign: 0x80674f,
  palm_tree: 0x6f9e86,  // palm green (fronds)
  beach_rock: 0xa0a0a0, // worn grey stone
  tide_pool: 0x4a9acf,  // aqua water
  trinket: 0xf5e3b0,    // sand gold (shell)
  pig_pen: 0xa0856a,    // weathered wood rails
  village_house: 0xd4a868, // warm amber walls
  village_well: 0x8a8a8a,  // grey stone
  lantern_post: 0xffcc00,  // warm amber glow (glass + halo)
  dock: 0x8b7355,          // wheat-brown planks
  autumn_leaf: 0xcc6600,   // burnt orange
  dead_bracken: 0x3a2a1a,  // dark brown stems (spec)
  moor_stone: 0x55665b,    // grey-green stone (spec)
  mist_shelf: 0x55665b,    // grey-green ledge (spec — same stone family as moor_stone)
  fallen_log: 0x3a2a1a,    // dark brown log (spec — same wood family as dead_bracken)
  gate: 0x4a3a2a,          // dark weathered wood (spec)
  journal_page: 0xe8d8a0,  // aged-yellow parchment (spec)
  colonnade_column: 0x1a2a18, // dark-green stone (spec)
  broken_pillar: 0x1a2a18,    // dark-green stone (spec — same stone family as colonnade)
  dais: 0x2a3a28,              // dark-green stone (spec — lighter than colonnade, ornate centerpiece)
  spiral_ledge: 0x1a2a18,      // dark-green stone (spec — same stone family as colonnade)
  bioluminescent_fungus: 0x76985a, // pale subterranean lichen (was 0x00ff7f neon)
  gnarled_tree: 0x304139,          // Lanternwood dark green wood, readable on #111b1c
};

/** Per-biome accent palette for props shared across visually different maps. */
export const BIOME_ACCENTS: Record<string, {
  sand: number;
  tidePoolStyle?: 'beach' | 'flooded';
}> = {
  pig_beach: { sand: 0xd6b274 }, // warm sand (spec: tide pool rim / rock base)
  // Cold road-water over dark masonry/mud — deliberately no beach sea stars.
  drowned_road: { sand: 0x34484d, tidePoolStyle: 'flooded' },
  moonfall_hollow: { sand: 0x20343e, tidePoolStyle: 'flooded' },
};
